import React, { useEffect, useState, useMemo } from "react";
import SelectOption from "./SelectOption";
import { ReactComponent as AddIcon } from "../../../Assets/Icons/addNewIcon.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/Icons/info.svg";
import { ReactComponent as WhitePlainTick } from "../../../Assets/Icons/whiteplaintick.svg";
import { Tooltip } from "antd";
import Input from "../FormInputs";
import { useDispatch, useSelector } from "react-redux";
import { createOffer, createSubCategory } from "../../../services/offerService";
import { useFormik } from "formik";
import PrimaryButton from "../PrimaryButton";
import GreyButton from "../GreyButton";
import { subCategorySchema } from "../../../utils/validationSchemas";
import declinedIcon from "../../../Assets/Icons/declinedIcon.svg";
import pendingIcon from "../../../Assets/Icons/pendingIcon.svg";
import toast from "../../../utils/toast";

const SubCategorySelection = ({
  categoryId,
  subCategories,
  setSubCategories,
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  categories,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  console.log(offerData, "offerDataofferDataofferData");

  // Get the selected category name for dynamic title
  const selectedCategoryName = useMemo(() => {
    // First try to get from editOfferData (edit mode)
    if (isEdit && editOfferData?.category?.title) {
      return editOfferData.category.title;
    }
    // Then try to get from offerData (new offer creation)
    if (offerData?.category?.title) {
      return offerData.category.title;
    }
    // Fallback: look up in categories array by categoryId
    if (categoryId && categories?.length > 0) {
      const category = categories.find((cat) => cat?._id === categoryId);
      return category?.title || "";
    }
    return "";
  }, [isEdit, editOfferData?.category?.title, offerData?.category?.title, categoryId, categories]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedSubCategoriesIds, setSelectedSubCategoriesIds] = useState([]);
  const [addClicked, setAddClicked] = useState(false);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [inputNonce, setInputNonce] = useState(0);
  const [subNames, setSubNames] = useState([]);
  const [approvedSubCategories, setApprovedSubCategories] = useState(
    subCategories?.filter((sub) => sub?.status === "approved")
  );
  const [offerPendingSub, setOfferPendingSub] = useState([]);
  const [isAddingSubCategory, setIsAddingSubCategory] = useState(false);

  useEffect(() => {
    // Logic to determine which data source to use:
    // 1. If we are editing, ordinarily we might look at editOfferData.
    // 2. BUT, if we have made changes in this session (persisted in offerData Redux state),
    //    and that offerData corresponds to the same offer ID, we should prioritize offerData.
    // 3. For new offers (!isEdit), we always use offerData.

    let sourceData = offerData;

    if (isEdit && editOfferData) {
      // If the Redux state (offerData) matches the offer being edited AND has potentially newer data, use it.
      // Otherwise, fall back to the initial editOfferData.
      if (offerData?._id === editOfferData?._id && Array.isArray(offerData?.subCategory)) {
        sourceData = offerData;
      } else {
        sourceData = editOfferData;
      }
    }

    // Reset relevant states before repopulating to avoid duplication
    setSelectedSubCategories([]);
    setSelectedSubCategoriesIds([]);
    setSubNames([]);
    setFieldValue("subCategory", []);
    setOfferPendingSub([]);

    if (sourceData?.subCategory) {
      sourceData.subCategory.forEach((s) => {
        if (s?.status === "pending" || s?.status === "rejected") {
          setOfferPendingSub((prev) => {
            const exists = prev.some(p => p._id === s._id);
            return exists ? prev : [...prev, s];
          });
        }
        // We need to re-select items. 
        // Note: handleClick toggles, so we need to be careful not to toggle OFF if called multiple times or incorrectly.
        // But since we wiped state above, toggling ON is safe if we do it once per item.
        // HOWEVER, handleClick is asynchronous in state updates (using prev), so calling it in a loop might be risky if not batched.
        // Better to batch update state instead of calling handleClick in map.
      });

      // Batch update state for stability
      const newSelected = [];
      const newIds = [];
      const newNames = [];

      sourceData.subCategory.forEach(s => {
        // Check if we already added it (dedupe)
        if (!newIds.includes(s._id)) {
          newSelected.push(s);
          newIds.push(s._id);
          newNames.push(s.title);
        }
      });

      setSelectedSubCategories(newSelected);
      setSelectedSubCategoriesIds(newIds);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);
    }
  }, [offerData?._id, editOfferData, isEdit]); // Reduced dependencies to avoid infinite loops if offerData changes structure but not ID

  // Normalize and split predefined vs custom for clear UI
  const normalizeTitle = (t) => String(t || "").trim().toLowerCase();
  const isCustomSub = (s) =>
    s?.isCustom === true || s?.merchantId || s?.merchantid || s?.merchant?._id;
  const uniqueByTitle = (arr) => {
    const seen = new Set();
    const out = [];
    for (const item of arr || []) {
      const key = normalizeTitle(item?.title);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  };

  const allApproved = uniqueByTitle(
    (subCategories || []).filter((sub) => sub?.status === "approved")
  );
  const allCustomApproved = uniqueByTitle(allApproved.filter(isCustomSub));
  const customTitles = new Set(allCustomApproved.map((s) => normalizeTitle(s.title)));
  const generalApproved = uniqueByTitle(
    allApproved.filter((s) => !customTitles.has(normalizeTitle(s.title)))
  );

  const initialValues = {
    subCategory: [],
  };

  const payload = {
    category: categoryId,
    merchantId: selectedMerchant?._id,
    subCategory: selectedSubCategoriesIds,
    id: offerData?._id,
    isCreated: false,
  };

  useEffect(() => {
    setPayloadData(payload);
  }, [categoryId, selectedSubCategoriesIds, offerData?._id]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: subCategorySchema,

    onSubmit: async (values) => {
      const hasRejectedSubCategory = selectedSubCategories?.some(
        (subCat) => subCat.status === "rejected"
      );
      if (hasRejectedSubCategory) {
        toast?.error("You cannot proceed with rejected sub category selected.");
        return;
      }
      const createPromise = await dispatch(
        createOffer({ ...payload, offerId: offerData?._id })
      )?.unwrap();
      if (createPromise?.success === true) {
        setStepIndex(stepIndex + 1);
      }
    },
  });

  const { subCategory } = formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    setValues,
  } = formik;

  const scrollToFirstError = (formErrors) => {
    const priority = ["subCategory"];
    let targetField = priority.find((key) => formErrors && Object.prototype.hasOwnProperty.call(formErrors, key));
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    if (!targetField) return;
    let el = window.document.querySelector(`[data-field="${targetField}"]`) || window.document.querySelector(`[name="${targetField}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") { try { el.focus(); } catch (_) { } }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const attemptNext = async () => {
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(Object.keys(validationErrors).reduce((acc, k) => { acc[k] = true; return acc; }, {}), true);
      scrollToFirstError(validationErrors);
      return;
    }
    handleSubmit();
  };

  const handleSelectAll = () => {
    // Check if all approved subcategories (both predefined and custom) are selected
    const allApprovedSubCategories = [...generalApproved, ...allCustomApproved];
    const allSelected = allApprovedSubCategories?.length > 0 &&
      allApprovedSubCategories.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));

    if (allSelected) {
      // Deselect all approved subcategories (both predefined and custom)
      const updatedSubCategories = selectedSubCategories.filter(
        (subCat) => !allApprovedSubCategories.some((g) => g?._id === subCat?._id)
      );
      setSelectedSubCategories(updatedSubCategories);

      const updatedIds = selectedSubCategoriesIds.filter(
        (id) => !allApprovedSubCategories.some((g) => g?._id === id)
      );
      setSelectedSubCategoriesIds(updatedIds);

      const updatedNames = subNames.filter(
        (name) => !allApprovedSubCategories.some((g) => g?.title === name)
      );
      setSubNames(updatedNames);
      setFieldValue("subCategory", updatedNames);
    } else {
      // Select all approved subcategories (both predefined and custom)
      const newSubCategories = [...selectedSubCategories];
      const newIds = [...selectedSubCategoriesIds];
      const newNames = [...subNames];

      allApprovedSubCategories.forEach((subCat) => {
        const exists = newSubCategories.some((s) => s?._id === subCat?._id);
        if (!exists) {
          newSubCategories.push(subCat);
          newIds.push(subCat?._id);
          newNames.push(subCat?.title);
        }
      });

      setSelectedSubCategories(newSubCategories);
      setSelectedSubCategoriesIds(newIds);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);
    }
  };

  const handleClick = (id, title) => {
    const isSelected = selectedSubCategories.find(
      (subCat) => subCat?._id === id
    );

    if (isSelected) {
      const updatedSubCategories = selectedSubCategories.filter(
        (subCat) => subCat?._id !== id
      );
      setSelectedSubCategories(updatedSubCategories);

      const updatedIds = selectedSubCategoriesIds.filter(
        (subCategoryId) => subCategoryId !== id
      );
      setSelectedSubCategoriesIds(updatedIds);

      const updatedNames = subNames.filter((t) => t !== title);
      setSubNames(updatedNames);
      setFieldValue("subCategory", updatedNames);
    } else {
      const subCategory = subCategories?.find((subCat) => subCat?._id === id);
      setSelectedSubCategories((prev) => [...prev, subCategory]);
      setSelectedSubCategoriesIds((prev) => [...prev, subCategory?._id]);
      setSubNames((prev) => [...prev, subCategory?.title]);
      setFieldValue("subCategory", [...subNames, subCategory?.title]);
    }
  };

  const isSubCategoryClicked = (subCat) => {
    return selectedSubCategories.find(
      (selectedCat) => selectedCat?._id === subCat?._id
    );
  };

  const handleAddClick = () => {
    setNewSubCategory("");
    setInputNonce((n) => n + 1);
    setAddClicked(true);
  };

  const handleNewSubCategoryChange = (e) => {
    setNewSubCategory(e.target.value);
  };

  const handleNewSubCategorySubmit = async () => {
    const titleToCreate = (newSubCategory || "").trim();
    if (titleToCreate !== "") {
      setIsAddingSubCategory(true);
      try {
        const payload = role === "merchant" ? {
          title: titleToCreate,
          status: true,
          category: categoryId,
          offer: offerData?._id,
          isCustom: true,
        } : {
          title: titleToCreate,
          status: true,
          category: categoryId,
          offer: offerData?._id,
          isCustom: true,
          merchantId: offerData?.merchant?._id,
        };
        const createPromise = await dispatch(
          createSubCategory(payload)
        )?.unwrap();
        if (createPromise?.success === true) {
          const created = createPromise?.data || {};
          // Only add to pending list if really pending/rejected
          if (created?.status === "pending" || created?.status === "rejected") {
            setOfferPendingSub((prev) => {
              const exists = (prev || []).some((p) => p?._id === created?._id);
              return exists ? prev : [...(prev || []), created];
            });
          }
          // Merge into subCategories without duplicates (by _id or normalized title)
          const norm = (t) => String(t || "").trim().toLowerCase();
          setSubCategories((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.some((s) => s?._id === created?._id || norm(s?.title) === norm(created?.title));
            return exists ? list : [...list, created];
          });
          setAddClicked(false);
          setSelectedSubCategories((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.some((s) => s?._id === created?._id);
            return exists ? list : [...list, created];
          });
          setSelectedSubCategoriesIds((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.includes(created?._id);
            return exists ? list : [...list, created?._id];
          });
          setSubNames((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.includes(created?.title);
            return exists ? list : [...list, created?.title];
          });
          {
            const currentList = Array.isArray(formik.values?.subCategory)
              ? formik.values.subCategory
              : [];
            const exists = currentList.includes(created?.title);
            const nextList = exists ? currentList : [...currentList, created?.title];
            setFieldValue("subCategory", nextList);
          }
          setNewSubCategory("");
          setInputNonce((n) => n + 1);
        }
      } catch (error) {
        console.error("Error creating subcategory:", error);
      } finally {
        setIsAddingSubCategory(false);
      }
    }
  };

  const isNextButtonBusy = Boolean(loading);
  const isNextButtonDisabled = isNextButtonBusy ||
    selectedSubCategoriesIds.length === 0 ||
    !formik.values.subCategory ||
    (Array.isArray(formik.values.subCategory) && formik.values.subCategory.length === 0);
  const nextButtonLabel = isNextButtonBusy ? (
    <span className="flex items-center justify-center gap-2">
      <span className="offer-btn-spinner" />
      <span>Processing...</span>
    </span>
  ) : (
    "Next"
  );

  const addButtonLabel = isAddingSubCategory ? (
    <span className="flex items-center justify-center gap-2">
      <span className="offer-btn-spinner" />
      <span>Adding...</span>
    </span>
  ) : (
    "Add"
  );

  return (
    <div className="w-full flex flex-col items-center justify-center mt-20">
      <div className="sm:w-[100%] md:w-[80%] lg:w-[70%] xl:w-[55%] ">
        <p className="font-poppins font-[500] sm:text-[12px] md:text-[14px] lg:text-[16px] text-[#303030]">
          {selectedCategoryName ? (
            <>
              Which of the below does the{" "}
              <span className="font-[700] text-[#000000]">
                {selectedCategoryName}
              </span>{" "}
              Offer relate to?
            </>
          ) : (
            "Which of the below does this offer relate to?"
          )}
        </p>
        {/* Select All Button */}
        <div className="mt-3 mb-5" data-field="subCategory">
          <button
            onClick={handleSelectAll}
            className={`flex items-center gap-2 cursor-pointer border-0 bg-transparent p-0 ${(() => {
                const allApprovedSubCategories = [...generalApproved, ...allCustomApproved];
                return allApprovedSubCategories?.length > 0 &&
                  allApprovedSubCategories.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));
              })()
                ? "text-[#00ADEF]"
                : "text-[#303030]"
              }`}
          >
            <div className="flex items-center justify-center">
              {(() => {
                const allApprovedSubCategories = [...generalApproved, ...allCustomApproved];
                return allApprovedSubCategories?.length > 0 &&
                  allApprovedSubCategories.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));
              })() ? (
                <div className="w-[18px] h-[18px] sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px] border border-[#00ADEF] rounded-[3px] bg-[#00ADEF] flex items-center justify-center">
                  <WhitePlainTick className="w-[12px] h-[12px] sm:w-[10px] sm:h-[10px] md:w-[12px] md:h-[12px]" />
                </div>
              ) : (
                <div className="w-[18px] h-[18px] sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px] border border-[#D9D9D9] rounded-[3px] bg-white" />
              )}
            </div>
            <p className={`font-poppins font-[400] sm:text-[12px] md:text-[14px] leading-[21px] ${(() => {
                const allApprovedSubCategories = [...generalApproved, ...allCustomApproved];
                return allApprovedSubCategories?.length > 0 &&
                  allApprovedSubCategories.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));
              })()
                ? "text-[#00ADEF]"
                : "text-[#303030]"
              }`}>
              Select All
            </p>
          </button>
        </div>
        <div className="mt-0" data-field="subCategory">
          {generalApproved?.length > 0 &&
            generalApproved?.map((subCat) => (
              <SelectOption
                key={subCat?._id}
                icon={null}
                title={subCat?.title}
                clicked={isSubCategoryClicked(subCat)}
                handleClick={() => handleClick(subCat?._id, subCat?.title)}
                postIcon={null}
                showCheckmark={true}
              />
            ))}
          {/* Custom approved sub-categories section */}
          <div className="h-[1px] bg-[#E6E6E6] w-full my-6" />
          <p className="font-poppins font-[500] sm:text-[11px] md:text-[13px] lg:text-[15px] text-[#303030] mt-1 mb-3">Custom Services</p>
          {allCustomApproved?.length > 0 ? (
            allCustomApproved.map((subCat) => (
              <SelectOption
                key={subCat?._id || subCat?.title}
                icon={null}
                title={subCat?.title}
                clicked={isSubCategoryClicked(subCat)}
                handleClick={() => handleClick(subCat?._id, subCat?.title)}
                postIcon={null}
                showCheckmark={true}
              />
            ))
          ) : (
            <div className="text-[#818181] font-poppins sm:text-[10px] md:text-[12px] mt-2">
              No custom sub-categories added yet.
            </div>
          )}
          {offerPendingSub?.length > 0 &&
            offerPendingSub?.map((subCat) => (
              <div key={subCat?._id}>
                <SelectOption
                  icon={null}
                  title={subCat?.title}
                  clicked={isSubCategoryClicked(subCat)}
                  handleClick={() => handleClick(subCat?._id, subCat?.title)}
                  postIcon={null}
                  showCheckmark={true}
                />
              </div>
            ))}
          {generalApproved?.length == 0 &&
            offerPendingSub?.length == 0 &&
            offerPendingSub?.length == 0 && (
              <p className="font-poppins font-[500] sm:text-[12px] md:text-[14px] lg:text-[16px] text-[#303030] text-center">
                The Selected Category does not have any sub category to
                select!!!
              </p>
            )}
        </div>
        {errors?.subCategory && touched?.subCategory ? (
          <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1">
            {errors?.subCategory}
          </span>
        ) : null}
        <div
          className="mt-5 flex items-center gap-2 cursor-pointer"
          onClick={handleAddClick}
        >
          <AddIcon />
          <p className="font-poppins font-[400] sm:text-[12px] md:text-[14px] text-[#00ADEF] text-center leading-[18px]">
            Add another type of service
          </p>
          <Tooltip
            title="Add a custom sub-category that is not available in the list above."
            color="#8C8C8C"
            placement="top"
          >
            <InfoIcon
              className="w-[14px] h-[14px] sm:w-[12px] sm:h-[12px] md:w-[14px] md:h-[14px] cursor-pointer hover:opacity-80 transition-opacity info-icon-grey"
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </div>
        {addClicked && (
          <div className="mt-5">
            <Input
              type="text"
              placeholder="Enter Custom Service"
              width="100%"
              radius="5px"
              InputDivClass="w-[100%] mb-4"
              name="newSubCategory"
              key={inputNonce}
              value={newSubCategory}
              handleChange={handleNewSubCategoryChange}
              handleKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleNewSubCategorySubmit();
                }
              }}
            />
            <div className="w-full text-end">
              <PrimaryButton
                className="h-[45px] w-[40%] lg:w-[25%] rounded-[5px] text-[16px] font-[500] font-montserrat"
                label={addButtonLabel}
                onClick={handleNewSubCategorySubmit}
                disabled={!newSubCategory.trim() || isAddingSubCategory}
              />
            </div>
          </div>
        )}
        <div className="w-full text-end mt-28">
          <GreyButton
            className="w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
            label="Previous"
            onClick={handlePrevious}
          />
          <PrimaryButton
            className={
              "w-[40%] lg:w-[25%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat ms-2"
            }
            label={nextButtonLabel}
            disabled={isNextButtonDisabled}
            onClick={async () => {
              // If user started adding a custom sub-category, auto-save it before Next
              if (addClicked && (newSubCategory || "").trim().length > 0) {
                await handleNewSubCategorySubmit();
              }
              attemptNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SubCategorySelection;
